import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import popularCitiesData from '../../data/popularCitiesData.json';
import { MapPin } from 'lucide-react';

const PopularCities = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cities = Object.entries(popularCitiesData)?.map(([slug, data]) => ({
    slug,
    name: data?.name,
    state: data?.state,
    description: data?.description,
    image: data?.image,
    imageAlt: data?.imageAlt,
    highlights: data?.highlights
  }));

  const handleCityClick = (citySlug) => {
    navigate(`/city-detail/${citySlug}`);
    window.scrollTo(0, 0);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Popular Cities for Taxi Services",
    "description": "Top taxi services available in major cities across North India",
    "itemListElement": cities?.map((city, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Place",
        "name": city?.name
      }
    }))
  };

  return (
    <>
      <SEO
        title="Popular Cities - Best Taxi Services in Ludhiana, Chandigarh, Delhi | Advait Tour and Travels"
        description="Explore premium taxi services in popular cities across North India. Book reliable cabs in Ludhiana, Chandigarh, Delhi, Shimla, Manali, Amritsar. 24/7 service available from Advait Tour and Travels."
        keywords="taxi service ludhiana, cab booking chandigarh, delhi taxi, shimla cab service, manali taxi booking, amritsar cab, ludhiana to delhi taxi, chandigarh to shimla cab"
        canonicalUrl={`${window.location?.origin}/popular-cities`}
        ogTitle="Popular Cities - Best Taxi Services | Advait Tour and Travels"
        ogDescription="Book premium taxi services in major cities. Ludhiana taxi service, Chandigarh cab booking, Delhi to Ludhiana taxi. 24/7 availability."
        ogUrl={`${window.location?.origin}/popular-cities`}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <Header />
        
        <main className="py-12 md:py-16 lg:py-20">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-orange-500 to-blue-600 mb-4">
                Popular Cities for Taxi Services
              </h1>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Advait Tour and Travels offers premium taxi services in major cities across North India. 
                Book reliable cabs with 24/7 availability and experienced drivers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {cities?.map((city, index) => (
                <div
                  key={city?.slug}
                  onClick={() => handleCityClick(city?.slug)}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer hover-lift border-2 border-transparent hover:border-blue-400 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative h-64 overflow-hidden image-zoom">
                    <img
                      src={city?.image}
                      alt={city?.imageAlt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white mb-1 transition-transform duration-300 group-hover:translate-x-2">
                        {city?.name}
                      </h3>
                      <p className="text-blue-200 text-sm flex items-center gap-1 transition-transform duration-300 group-hover:translate-x-2">
                        <MapPin size={14} />
                        {city?.state}
                      </p>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {city?.description}
                    </p>
                    {city?.highlights && city?.highlights?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Services Available:</p>
                        <div className="flex flex-wrap gap-2">
                          {city?.highlights?.slice(0, 3)?.map((highlight, idx) => (
                            <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full transition-all duration-300 hover:bg-blue-200 hover:scale-105">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PopularCities;